<template>
  <div class="gallery-toolbar-container">
    <nav v-if="!isMobile" class="gallery-toolbar navbar navbar-expand-lg" tabindex="-1">
      <ul class="navbar-nav mr-auto">
        <li v-for="(item, index) in multiButtonItems" :key="index" class="nav-item" @click="item.method()">
          <b-btn variant="link" class="tool-link">
            <i class="icon in-circle" :class="item.icon" />
            <span class="button-text">{{ $getText(item.text) }}</span>
          </b-btn>
        </li>
      </ul>
    </nav>
    <multi-button-mobile
      v-else-if="isMultiButtonMobileVisible && !selectMode"
      :items="multiButtonItems"
      @createAlbum="createAlbum()"
      @editAlbum="editAlbum()"
      @deleteAlbum="handleDeleteAlbum()"
      @activateDownloadMedia="activateDownloadMedia()"
      @addMedia="addMedia()"
    />
    <AulaModal
      ref="deleteAlbumModal"
      ok-text="BUTTON_YES"
      @okClicked="deleteAlbum"
      @cancelClicked="$refs.deleteAlbumModal.hide()"
    >
      {{ $getText('GALLERY_CONFIRM_TO_DELETE_ALBUM_1') }}
      <strong>{{ $getText('GALLERY_CONFIRM_TO_DELETE_ALBUM_2') }}</strong
      ><br />
      {{ $getText('GALLERY_CONFIRM_TO_DELETE_ALBUM_3') }}
    </AulaModal>
    <AulaModal
      ref="stepUpModal"
      ok-text="BUTTON_YES"
      @okClicked="redirectToStepUp"
      @cancelClicked="$refs.stepUpModal.hide()"
    >
      {{ $t('warning.action_step_up_required') }}
      <br />
      {{ $t('warning.continue') }}
    </AulaModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { types } from '../../store/types/types';
import MultiButtonMobile from '../shared/MultiButtonMobile';
import { parentTypes } from '../../../shared/enums/parentTypes.ts';
import { permissionEnum } from '../../../shared/enums/permissionEnum.ts';
import { useUserProfileStore } from '../../../shared/stores';
import { filterGroupMembershipInstitutions } from '../../../shared/utils/institutionUtil';

export default {
  components: {
    MultiButtonMobile,
  },
  props: {
    parent: { type: String, default: parentTypes.PROFILE },
    mediaNumber: { type: Number, default: 0 },
    selectMode: { type: Boolean, default: false },
  },
  computed: {
    ...mapGetters({
      isPreviewMode: types.GET_IS_PREVIEW_MODE,
      isMobile: types.GET_IS_MOBILE,
      isTablet: types.GET_IS_TABLET,
      userGroup: types.GET_USER_GROUPS,
      activeGroup: types.GET_ACTIVE_GROUP,
      albums: types.GET_ALBUMS,
      institutions: types.GET_INSTITUTIONS,
      hasPermissionGroup: types.HAS_PERMISSION_ON_GROUP,
      hasPermissionOnInstitution: types.HAS_PERMISSION_ON_INSTITUTION,
      hasPermission: types.HAS_PERMISSION,
      limitMedia: types.GET_LIMIT_MEDIA,
      activeAlbum: types.GET_ACTIVE_ALBUM,
      hasNonBlockingProfiles: types.GET_HAS_NON_BLOCKING_PROFILES,
      isSteppedUp: types.GET_GLOBAL_STEPPED_UP,
    }),
    userProfileStore() {
      return useUserProfileStore();
    },
    profile() {
      return this.userProfileStore.institutionProfile;
    },
    isCreator() {
      return this.institutions.some(inst => inst.institutionProfileId === this.activeAlbum.creator.id);
    },
    isStepUpRequired() {
      if (this.isCreator) {
        return false;
      }
      return !this.isSteppedUp;
    },
    isMultiButtonMobileVisible() {
      const hiddenButtonAreas = ['group-create-album', 'group-edit-album', 'group-add-media'];
      return hiddenButtonAreas.includes(this.$route.name) === false;
    },
    multiButtonItems() {
      let multiButtonItems = [];
      const defaultMenuList = [];

      if (this.canCreateAlbum()) {
        defaultMenuList.push({
          icon: this.isMobile ? 'icon-Aula_picture' : 'icon-Aula_plus',
          text: 'GALLERY_TOOLBAR_NEW_ALBUM',
          action: 'createAlbum',
          method: !this.isPreviewMode ? this.createAlbum : null,
        });
      }

      const albumMenu = [
        {
          icon: 'el-icon-download',
          text: 'GALLERY_TOOLBAR_DOWNLOAD_ALBUM',
          action: 'activateDownloadMedia',
          method: !this.isPreviewMode ? this.activateDownloadMedia : null,
        },
      ];

      if (this.activeAlbum) {
        if (this.activeAlbum.currentUserCanEdit) {
          albumMenu.push({
            icon: 'icon-Aula_picture',
            text: 'GALLERY_EDIT_ALBUM',
            action: 'editAlbum',
            method: !this.isPreviewMode ? this.editAlbum : null,
          });
        }
        if (this.activeAlbum.currentUserCanAddMedia && this.limitMedia > this.mediaNumber) {
          albumMenu.push({
            icon: 'icon-Aula_picture',
            text: 'GALLERY_ADD_MEDIA_TO_ALBUM',
            action: 'addMedia',
            method: !this.isPreviewMode ? this.addMedia : null,
            permission: null,
          });
        }
        if (this.activeAlbum.currentUserCanDelete) {
          albumMenu.push({
            icon: 'icon-Aula_bin',
            text: 'GALLERY_DELETE_ALBUM',
            action: 'deleteAlbum',
            method: !this.isPreviewMode ? this.handleDeleteAlbum : null,
          });
        }
      }

      if (this.$route.name == 'gallery' || this.$route.name == 'group-gallery') {
        multiButtonItems = defaultMenuList;
      } else if (this.$route.name == 'myMedias') {
        multiButtonItems = [albumMenu[0]];
      } else {
        multiButtonItems = albumMenu;
      }
      return multiButtonItems;
    },
  },
  methods: {
    ...mapActions({
      getAlbumMedia: types.ACTION_GET_ALBUM_DETAILS,
      deleteAlbumByIds: types.DELETE_ALBUM,
    }),
    canCreateAlbum() {
      const isBlockCommunication = this.profile.communicationBlock && !this.hasNonBlockingProfiles;
      if (isBlockCommunication) {
        return false;
      }
      if (this.parent === parentTypes.GROUP) {
        const memberInstitutions = filterGroupMembershipInstitutions(this.institutions, this.activeGroup);
        const isBlockCommunication = memberInstitutions.length > 0 && memberInstitutions.every(inst => inst.communicationBlock);
        return (
          !isBlockCommunication &&
          (this.hasPermissionGroup(permissionEnum.SHARE_MEDIA, this.activeGroup.id) ||
            this.hasPermissionOnInstitution(
              permissionEnum.USE_GROUPS_AS_DISTRIBUTION_LISTS,
              this.activeGroup.institutionCode
            ))
        );
      } else {
        return this.hasPermission(permissionEnum.SHARE_MEDIA) || this.hasPermission(permissionEnum.HANDLE_MEDIA);
      }
    },
    createAlbum() {
      if (this.parent == parentTypes.GROUP) {
        this.$router.push({
          name: 'group-create-album',
          params: { groupId: this.activeGroup.id },
        });
      } else {
        this.$router.push({ name: 'createAlbum' });
      }
    },
    editAlbum() {
      if (this.parent == parentTypes.GROUP) {
        this.$router.push({
          name: 'group-edit-album',
          params: {
            groupId: this.activeGroup.id,
            albumId: this.$route.params.albumId,
          },
        });
      } else {
        this.$router.push({
          name: 'editAlbum',
          params: { albumId: this.$route.params.albumId },
        });
      }
    },
    handleDeleteAlbum() {
      if (this.isStepUpRequired) {
        this.showStepUpWarning();
        return;
      }

      this.$refs.deleteAlbumModal.show();
    },
    showStepUpWarning() {
      this.$refs.stepUpModal.show();
    },
    redirectToStepUp() {
      localStorage.setItem('redirectedurlafterstepup', window.location.href);
      localStorage.setItem('redirectedurlbeforestepup', window.location.href);
      window.location.href = '/auth/stepup.php';
    },
    async deleteAlbum() {
      let result = false;

      try {
        result = await this.deleteAlbumByIds({ albumIds: [this.$route.params.albumId] });
      } catch {
        this.$showToast(this.$t('gallery.delete_error_single'), 'error');
      }

      this.$showToast(
        result ? this.$t('gallery.delete_success_single') : this.$t('gallery.delete_error_single'),
        'error'
      );

      if (this.parent == parentTypes.GROUP) {
        this.$router.push({
          name: 'group-gallery',
          params: { groupId: this.activeGroup.id },
        });
      } else {
        this.$router.push({ name: 'gallery' });
      }
    },
    addMedia() {
      if (this.parent == parentTypes.GROUP) {
        this.$router.push({
          name: 'group-add-media',
          params: {
            groupId: this.activeGroup.id,
            albumId: this.$route.params.albumId,
          },
        });
      } else {
        this.$router.push({
          name: 'addMedia',
          params: { albumId: this.$route.params.albumId },
        });
      }
    },
    activateDownloadMedia() {
      this.$emit('activateDownloadMedia');
    },
  },
};
</script>

<style scoped lang="scss">
@use '../../../shared/assets/scss/core/variables' as *;
@use '../../../shared/assets/scss/core/breakpoints' as *;
@use '../../../shared/assets/scss/components/gallery/toolbar' as *;

.navbar-nav {
  display: flex;
  gap: 4px;
}
.tool-link {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px;
  border-radius: 4px;
  height: 38px;
  transition: background-color 150ms ease-in;
}
</style>
